// AI 챗봇 페이지 (PC 버전)
import { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Send, Bot, User, Loader2, ArrowRight, FileText } from "lucide-react";
import api from "../../api/axios";
import PageHeader from "../../components/common/ui/PageHeader";

export default function AIChat() {
  const { id: projectId } = useParams();
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "안녕하세요! P6ix AI 어시스턴트입니다. 건설 관련 법령 정보나 PMIS 페이지 안내가 필요하시면 말씀해주세요.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await api.post("/chatbot/chat/", {
        message: userMessage,
      });

      const data = response.data;

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.response,
          routing: data.routing || [],
          references: data.references || [],
        },
      ]);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "죄송합니다. 응답을 가져오는 중 오류가 발생했습니다.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 h-[calc(100vh-180px)] flex flex-col">
      <PageHeader>P6ix AI 챗봇</PageHeader>

      {/* 메시지 영역 */}
      <div className="flex-1 overflow-y-auto bg-white border border-gray-200 rounded-lg p-6 mb-4 space-y-4">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            {msg.role === "assistant" && (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center flex-shrink-0">
                <Bot className="w-5 h-5 text-white" />
              </div>
            )}
            <div
              className={`max-w-[70%] rounded-lg px-4 py-3 ${msg.role === "user"
                  ? "bg-gradient-to-br from-purple-600 to-blue-600 text-white"
                  : "bg-gray-50 text-gray-900 border border-gray-200"
                }`}
            >
              <p className="whitespace-pre-wrap break-words">{msg.content}</p>

              {/* 라우팅 버튼 */}
              {msg.routing && msg.routing.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                  {msg.routing.map((route, i) => (
                    <a
                      key={i}
                      href={route.path.replace(/:id/g, projectId).replace(/{projectId}/g, projectId)}
                      className="block p-2 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900">{route.title}</span>
                        <ArrowRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </a>
                  ))}
                </div>
              )}

              {/* 참고 문서 */}
              {msg.references && msg.references.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs font-bold text-gray-600 mb-2 flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    참고 문서
                  </p>
                  {msg.references.map((ref, i) => (
                    <details key={i} className="mb-2 bg-white rounded border border-gray-200">
                      <summary className="cursor-pointer p-2 text-xs font-bold text-gray-700">
                        {ref.law_name} {ref.hierarchy}
                      </summary>
                      <div className="p-2 text-xs text-gray-600 border-t border-gray-200">
                        {ref.text}
                      </div>
                    </details>
                  ))}
                </div>
              )}
            </div>
            {msg.role === "user" && (
              <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-white" />
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex gap-3 justify-start">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center flex-shrink-0">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* 입력 영역 */}
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSend()}
          placeholder="메시지를 입력하세요..."
          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
        <button
          onClick={handleSend}
          disabled={!input.trim() || loading}
          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
